import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Table, Form, Modal, Badge, Spinner, Alert, Pagination } from 'react-bootstrap';
import apiService from '../services/api';

const Currencies = () => {
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCurrency, setEditingCurrency] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [saving, setSaving] = useState(false);

  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    is_active: true,
  });

  useEffect(() => {
    loadCurrencies();
  }, [currentPage, itemsPerPage]);

  const loadCurrencies = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await apiService.getCurrencies();
      if (response.success) {
        setCurrencies(response.data || []);
        setTotalPages(Math.ceil((response.data?.total || response.data?.length || 0) / itemsPerPage));
      } else {
        setError(response.error || 'Failed to load currencies');
      }
    } catch (error) {
      console.error('Error loading currencies:', error);
      setError('Failed to load currencies');
    } finally {
      setLoading(false);
    }
  };

  const handleShowModal = (currency = null) => {
    if (currency) {
      setEditingCurrency(currency);
      setFormData({
        name: currency.name || '',
        code: currency.code || '',
        is_active: currency.is_active !== undefined ? currency.is_active : true,
      });
    } else {
      setEditingCurrency(null);
      setFormData({
        name: '',
        code: '',
        is_active: true,
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCurrency(null);
    setFormData({
      name: '',
      code: '',
      is_active: true,
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();

    setSaving(true);
    setError('');

    try {
      const currencyData = {
        name: formData.name.trim(),
        code: formData.code.trim(),
        is_active: formData.is_active,
      };

      let response;
      if (editingCurrency) {
        response = await apiService.updateCurrency(editingCurrency.id, currencyData);
      } else {
        response = await apiService.createCurrency(currencyData);
      }

      if (response.success) {
        handleCloseModal();
        await loadCurrencies();
      } else {
        setError(response.error || 'Failed to save currency');
      }
    } catch (error) {
      console.error('Error saving currency:', error);
      setError('Failed to save currency');
    } finally {
      setSaving(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  // Sorting functionality
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <i className="bi bi-arrow-down-up text-muted ms-1"></i>;
    }
    return sortConfig.direction === 'asc'
      ? <i className="bi bi-arrow-up text-primary ms-1"></i>
      : <i className="bi bi-arrow-down text-primary ms-1"></i>;
  };

  // Sort currencies based on current sort configuration
  const sortedCurrencies = React.useMemo(() => {
    let sortableCurrencies = [...currencies];
    if (sortConfig.key) {
      sortableCurrencies.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle different data types
        switch (sortConfig.key) {
          case 'name':
          case 'code':
            aValue = (aValue || '').toString().toLowerCase();
            bValue = (bValue || '').toString().toLowerCase();
            break;
          case 'created_at':
          case 'last_updated_at':
            aValue = new Date(aValue || 0);
            bValue = new Date(bValue || 0);
            break;
          case 'is_active':
            aValue = aValue ? 1 : 0;
            bValue = bValue ? 1 : 0;
            break;
          default:
            aValue = aValue || '';
            bValue = bValue || '';
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableCurrencies;
  }, [currencies, sortConfig]);

  return (
    <div>
      <Row>
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>
              <i className="bi bi-coin me-2"></i>
              Currencies
            </h2>
            <Button variant="primary" onClick={() => handleShowModal()}>
              <i className="bi bi-plus-circle me-2"></i>
              Add Currency
            </Button>
          </div>
        </Col>
      </Row>

      {error && (
        <Row className="mb-4">
          <Col>
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              <i className="bi bi-exclamation-triangle me-2"></i>
              {error}
            </Alert>
          </Col>
        </Row>
      )}

      {/* Filter Section */}
      <Row className="mb-4">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Status Filter</Form.Label>
            <Form.Select>
              <option value="">All Currencies</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Items per page</Form.Label>
            <Form.Select
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
            >
              <option value="10">10 per page</option>
              <option value="25">25 per page</option>
              <option value="50">50 per page</option>
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col>
          <Card>
            <Card.Body className="p-0">
              {loading ? (
                <div className="text-center p-4">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </div>
              ) : currencies.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <i className="bi bi-coin display-1"></i>
                  <div className="mt-2">No currencies found</div>
                </div>
              ) : (
                <Table responsive hover className="mb-0">
                  <thead>
                    <tr>
                      <th
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('name')}
                      >
                        Currency Name
                        {getSortIcon('name')}
                      </th>
                      <th
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('code')}
                      >
                        Currency Code
                        {getSortIcon('code')}
                      </th>
                      <th
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('is_active')}
                      >
                        Status
                        {getSortIcon('is_active')}
                      </th>
                      <th
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('created_at')}
                      >
                        Created
                        {getSortIcon('created_at')}
                      </th>
                      <th
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('last_updated_at')}
                      >
                        Updated
                        {getSortIcon('last_updated_at')}
                      </th>

                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedCurrencies.map((currency) => (
                      <tr key={currency.id}>
                        <td>
                          <div className="fw-medium">{currency.name}</div>
                        </td>
                        <td>
                          <code>{currency.code}</code>
                        </td>
                        <td>
                          <Badge
                            bg={currency.is_active ? 'success' : 'secondary'}
                            className="text-uppercase"
                          >
                            {currency.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td>{currency.created_at ? apiService.formatDate(currency.created_at) : '-'}</td>
                        <td>{currency.last_updated_at ? apiService.formatDate(currency.last_updated_at) : '-'}</td>

                        <td>
                          <div className="d-flex gap-1">
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => handleShowModal(currency)}
                              title="Edit"
                            >
                              <i className="bi bi-pencil"></i>
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>

            {/* Pagination */}
            {totalPages > 1 && (
              <Card.Footer>
                <div className="d-flex justify-content-between align-items-center">
                  <div className="text-muted">
                    Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, currencies.length)} of {currencies.length} entries
                  </div>
                  <Pagination className="mb-0">
                    <Pagination.Prev
                      disabled={currentPage === 1}
                      onClick={() => handlePageChange(currentPage - 1)}
                    />
                    {[...Array(totalPages)].map((_, i) => (
                      <Pagination.Item
                        key={i + 1}
                        active={i + 1 === currentPage}
                        onClick={() => handlePageChange(i + 1)}
                      >
                        {i + 1}
                      </Pagination.Item>
                    ))}
                    <Pagination.Next
                      disabled={currentPage === totalPages}
                      onClick={() => handlePageChange(currentPage + 1)}
                    />
                  </Pagination>
                </div>
              </Card.Footer>
            )}
          </Card>
        </Col>
      </Row>

      {/* Create/Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingCurrency ? 'Edit Currency' : 'Add Currency'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSave}>
          <Modal.Body>
            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Currency Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="e.g. US Dollar"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Currency Code *</Form.Label>
                  <Form.Control
                    type="text"
                    name="code"
                    value={formData.code}
                    onChange={handleInputChange}
                    required
                    placeholder="e.g. USD"
                  />
                  <Form.Text className="text-muted">
                    3-letter ISO code (e.g. USD, EUR, GBP)
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                name="is_active"
                id="is_active"
                label="Active Currency"
                checked={formData.is_active}
                onChange={handleInputChange}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={saving}>
              {saving ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" className="me-2" />
                  Saving...
                </>
              ) : (
                'Save Currency'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default Currencies;
